import type { MvrCharacterType, MvrFormatRuleSpec, MvrLengthSpec } from 'api';

export interface MvrFormatBuildResult {
  regex: string | null;
  description: string;
  warnings: string[];
  blockingErrors: string[];
}

const CHARSET_CLASS: Record<MvrCharacterType, string> = {
  digits: '0-9',
  letters: 'A-Za-z',
  alphanumeric: 'A-Za-z0-9',
};

const CHARSET_TEST: Record<MvrCharacterType, RegExp> = {
  digits: /^[0-9]*$/,
  letters: /^[A-Za-z]*$/,
  alphanumeric: /^[A-Za-z0-9]*$/,
};

const CHAR_TYPE_LABEL: Record<MvrCharacterType, string> = {
  digits: 'contain only digits',
  letters: 'contain only letters',
  alphanumeric: 'contain only letters and digits',
};

const escapeLiteral = (text: string): string =>
  text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const cleanOptions = (options: string[] | null | undefined): string[] =>
  (options ?? []).map((option) => option.trim()).filter(Boolean);

const buildAlternation = (options: string[]): string => {
  const escaped = options.map(escapeLiteral);
  return escaped.length === 1 ? escaped[0] : `(?:${escaped.join('|')})`;
};

const joinWithOr = (options: string[]): string => {
  const quoted = options.map((option) => `"${option}"`);
  if (quoted.length <= 2) return quoted.join(' or ');
  return `${quoted.slice(0, -1).join(', ')}, or ${quoted[quoted.length - 1]}`;
};

const optionList = (label: string, options: string[]): string =>
  `"${label}" option${options.length > 1 ? 's' : ''} ${options.map((o) => `"${o}"`).join(', ')}`;

const joinWithAnd = (parts: string[]): string => {
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join('; ')}; and ${parts[parts.length - 1]}`;
};

const lengthLookahead = (length: MvrLengthSpec | null): string | null => {
  if (!length) return null;
  if (length.mode === 'exact') {
    return Number.isInteger(length.value) && length.value > 0
      ? `(?=.{${length.value}}$)`
      : null;
  }
  const min = length.min ?? 0;
  const max = length.max;
  if (min === 0 && max == null) return null;
  return max != null ? `(?=.{${min},${max}}$)` : `(?=.{${min},}$)`;
};

export const buildRegexAndDescription = (
  spec: MvrFormatRuleSpec,
): MvrFormatBuildResult => {
  const startsWith = cleanOptions(spec.startsWith);
  const endsWith = cleanOptions(spec.endsWith);
  const length = spec.length ?? null;
  const characterType = spec.characterType ?? null;

  const isEmpty =
    startsWith.length === 0 &&
    endsWith.length === 0 &&
    !length &&
    !characterType;
  if (isEmpty) {
    return { regex: null, description: '', warnings: [], blockingErrors: [] };
  }

  const warnings: string[] = [];
  const blockingErrors: string[] = [];

  const minStartLength = startsWith.length
    ? Math.min(...startsWith.map((option) => option.length))
    : 0;
  const minEndLength = endsWith.length
    ? Math.min(...endsWith.map((option) => option.length))
    : 0;
  const minCombinedLength = minStartLength + minEndLength;

  const lenLookahead = lengthLookahead(length);
  const lengthCap =
    length?.mode === 'exact'
      ? length.value
      : length?.mode === 'range'
        ? (length.max ?? null)
        : null;

  if (lengthCap != null && lengthCap < minCombinedLength) {
    blockingErrors.push(
      `${length?.mode === 'exact' ? 'Exact' : 'Maximum'} length (${lengthCap}) is shorter than the shortest possible combination of the "starts with"/"ends with" text (${minCombinedLength} characters) — no value could ever match.`,
    );
  }
  if (
    length?.mode === 'range' &&
    length.min != null &&
    length.max != null &&
    length.min > length.max
  ) {
    blockingErrors.push(
      'Minimum length cannot be greater than maximum length.',
    );
  }

  const flagCharacterTypeViolations = (label: string, options: string[]) => {
    if (!characterType || !options.length) return;
    const bad = options.filter(
      (option) => !CHARSET_TEST[characterType].test(option),
    );
    if (bad.length === options.length) {
      blockingErrors.push(
        `Every "${label}" option contains characters not allowed by the selected character type — no value could ever match.`,
      );
    } else if (bad.length) {
      warnings.push(
        `${optionList(label, bad)} contain${bad.length > 1 ? '' : 's'} characters not allowed by the selected character type and will never match.`,
      );
    }
  };
  flagCharacterTypeViolations('Starts with', startsWith);
  flagCharacterTypeViolations('Ends with', endsWith);

  const flagTooLongOptions = (
    label: string,
    options: string[],
    otherSideMinLength: number,
  ) => {
    if (lengthCap == null || blockingErrors.length) return;
    const tooLong = options.filter(
      (option) => option.length + otherSideMinLength > lengthCap,
    );
    if (tooLong.length && tooLong.length < options.length) {
      warnings.push(
        `${optionList(label, tooLong)} can never fit within the length limit and will never match.`,
      );
    }
  };
  flagTooLongOptions('Starts with', startsWith, minEndLength);
  flagTooLongOptions('Ends with', endsWith, minStartLength);

  const lookaheads: string[] = [];
  if (lenLookahead) lookaheads.push(lenLookahead);
  if (characterType) lookaheads.push(`(?=[${CHARSET_CLASS[characterType]}]+$)`);

  const startGroup = startsWith.length ? buildAlternation(startsWith) : '';
  const endGroup = endsWith.length ? buildAlternation(endsWith) : '';
  const regex = `^${lookaheads.join('')}${startGroup}.*${endGroup}$`;

  const descriptionParts: string[] = [];
  if (startsWith.length)
    descriptionParts.push(`start with ${joinWithOr(startsWith)}`);
  if (endsWith.length)
    descriptionParts.push(`end with ${joinWithOr(endsWith)}`);
  if (length?.mode === 'exact' && lenLookahead) {
    descriptionParts.push(
      `be exactly ${length.value} character${length.value === 1 ? '' : 's'} long`,
    );
  } else if (length?.mode === 'range' && lenLookahead) {
    const { min, max } = length;
    if (min != null && max != null)
      descriptionParts.push(`be between ${min} and ${max} characters long`);
    else if (min != null)
      descriptionParts.push(`be at least ${min} characters long`);
    else if (max != null)
      descriptionParts.push(`be at most ${max} characters long`);
  }
  if (characterType) descriptionParts.push(CHAR_TYPE_LABEL[characterType]);

  const description = descriptionParts.length
    ? `Must ${joinWithAnd(descriptionParts)}.`
    : '';

  return { regex, description, warnings, blockingErrors };
};
